/**
 * Vakansiya o'ng tugma menyusi: elementlar (features/carriers/menu.ts) + oynalar (driver yuborish, tahrirlash).
 * Sahifa `actions.dialogs` ni bir marta chizadi.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { absoluteUrl, copyText, openInNewTab } from "@/lib/clipboard";
import { useMe } from "@/features/auth/hooks";
import { can } from "@/features/auth/types";
import { useArchiveJob, usePatchJob } from "@/features/carriers/api";
import { jobMenu } from "@/features/carriers/menu";
import type { Job } from "@/features/carriers/types";
import type { MenuEntry } from "@/components/ui/context-menu";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { useActionFailure } from "@/pages/carriers/useCarrierActions";
import { JobFormModal } from "./JobFormModal";
import { SubmitDriverModal } from "./SubmitDriverModal";

export function useJobActions() {
  const me = useMe().data;
  const navigate = useNavigate();
  const { t } = useTranslation("menu");
  const { t: tc } = useTranslation();
  const patch = usePatchJob();
  const archive = useArchiveJob();
  const fail = useActionFailure();
  const [dialog, setDialog] = useState<{ kind: "edit" | "submit"; job: Job } | null>(null);

  const items = useCallback(
    (j: Job): MenuEntry[] => {
      if (!me) return [];
      const path = `/jobs/${j.id}`;
      return jobMenu(
        j,
        can(me, "carriers_edit"),
        {
          open: () => navigate(path),
          openNewTab: () => openInNewTab(path),
          copyLink: () => void copyText(absoluteUrl(path)),
          navigate: (p) => navigate(p),
          submitDriver: () => setDialog({ kind: "submit", job: j }),
          edit: () => setDialog({ kind: "edit", job: j }),
          setStatus: (status) =>
            patch.mutate(
              { id: j.id, status, version: j.version },
              { onSuccess: () => toast.success(t("statusChanged", { name: j.title, status: tc(`status.${status}`) })), onError: fail },
            ),
          archive: () =>
            void confirmDialog({ title: t("archiveTitle", { name: j.title }), description: t("job.archiveDesc"), confirmLabel: t("archive"), danger: true }).then(
              (ok) => ok && archive.mutate(j.id, { onSuccess: () => toast.success(t("archivedToast", { name: j.title })), onError: fail }),
            ),
        },
        t,
        tc,
      );
    },
    [me, navigate, patch, archive, fail, t, tc],
  );

  const close = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const dialogs = (
    <>
      {dialog?.kind === "edit" && <JobFormModal open onOpenChange={close} job={dialog.job} />}
      {dialog?.kind === "submit" && <SubmitDriverModal open onOpenChange={close} job={dialog.job} />}
    </>
  );
  return { items, dialogs };
}
